import { rootModuleId } from '@app/const';
import { defaultServiceContext } from '@app/contexts';
import {
  Dependency,
  DependencyType,
  DesignChannelType,
  DesignCommonModule,
  DesignCsvData,
  DesignFunction,
  DesignGraphQlSchema,
  DesignIntent,
  DesignClass,
  DesignItemType,
  DesignModule,
  DesignModuleLink,
  DesignModuleNode,
  DesignObject,
  ObjectClass,
  LanguageCode,
  ModuleExport,
  VariableScope
} from '@app/types';
import { createUuid } from '@app/utilities';
import { CommonModuleManager } from '@app/utilities/CommonModuleManager';
import { Utility } from '@app/utilities/Utility';

const { avsService } = defaultServiceContext;

export default function ImportModule(
  exports: ModuleExport
) {
  const mapping: Record<DependencyType, Record<string, string>> = {
    variable: {},
    intent: {},
    function: {},
    schema: {},
    csvData: {},
    module: {},
    object: {}
  };

  const commonModuleManager = new CommonModuleManager();

  /**
    *  CORE-34512: Initializes import id and name changes
    */

  const initializeChanges = () => {
    if (exports.node.props.isCommonModule){
      installCommonModule(exports.node);
      window.apm.setSerializedChanged();
      window.apm.refreshApplicationTree();
    } else {
      //update flow nodes ids and names
      updateObjectIdsAndNames(exports.node.versions[1].flow.nodes);
      //update flow links ids and names
      updateObjectIdsAndNames(exports.node.versions[1].flow.links);
      //update dependencies ids and names
      updateObjectIdsAndNames(exports._dependencies);

      // Prepare each dependency to be added to the design. Update prompts and module flows where applicable.
      exports._dependencies.forEach((dep) => {
        const shouldUpgradeOmniChannel = window.apm.serialized.omniChannel.isOmniChannel;

        // Check if prompt/variable needs OmniChannel upgrade
        // TODO: need to test with OmniChannel on main design and importing a non-OmniCHannel module
        if (shouldUpgradeOmniChannel){
          if ('_class' in dep.node){
            const depClass = dep.node._class;
            if (depClass === ObjectClass.prompt && dep.type === DesignItemType.object){
              window.apm.knowledgeBase.initializePromptChannelConfig(dep.node);
              updatePromptInstances(dep.node as DesignObject);
            }
            if (depClass === DesignClass.variable){
              if (!dep.node.props.config.scope){
                dep.node.props.config.scope = VariableScope.session;
              }
            }
          }
        }

        if (dep.type === DesignItemType.module){
          if ('versions' in dep.node){
            //update dependency module flow node ids
            updateObjectIdsAndNames(dep.node.versions[1].flow.nodes);
            //update dependency module flow link ids
            updateObjectIdsAndNames(dep.node.versions[1].flow.links);
          }
        }
        // CORE-34512: All imported items are technically new to the design, so we add them
        mapping[dep.type][dep.id] = 'create::' + dep.id;

      });
      setChanges();
    }
  };

  /**
    *  Update node ids and all references in the import
    * @param {array} nodes main node, flows, and dependencies
    */

  const updateObjectIdsAndNames = (
    nodes: (
      DesignModuleLink[]
      | DesignModuleNode[]
      | Dependency[]
    )
  ) => {
    nodes.forEach((node) => {
      const newUUID = createUuid();
      const currentNode = 'node'in node ? node.node : node;
      if (!currentNode) {
        return;
      }
      const nodeId = currentNode.id;

      // Exports is readonly so its properties must be what are updated
      exports._dependencies = JSON.parse((JSON.stringify(exports._dependencies)).replace(new RegExp(nodeId, 'gi'), newUUID));
      exports.node = JSON.parse((JSON.stringify(exports.node)).replace(new RegExp(nodeId, 'gi'), newUUID));

      // If node object isn't a link, we update its if it matches one in the application
      if ('type' in currentNode){
        const oldName = currentNode.name!;
        const nodeType = currentNode.type!;
        const renamedNode = updateDependencyName(currentNode, nodeType);
        // If name changes, we update all references to it
        if (renamedNode){
          exports._dependencies = getDepNameMatch(oldName, renamedNode.name!, nodeType);
          getFlowNameMatch(oldName, renamedNode.name!, nodeType, exports.node);
        }
      }
    });
  };

  /**
     * Update dependency name match in any node flow
    */
  const getFlowNameMatch = (oldName: string, newName: string, depType: string, exportNode: DesignModule) => {
    const oldVarNamePattern = new RegExp(`\\bapp.${oldName}\\b`, 'g');
    const oldIntentNamePattern = new RegExp(`\\b${oldName}\\b`, 'g');

    //Update all flow nodes with newName, where applicable
    exportNode.versions[1].flow.nodes.forEach((node) => {

      if (node.name === oldName){
        node.name = newName;
      }
      // If current dependency is a variable, update all references to it in the main export node module
      if (depType === DesignItemType.variable){
        // Custom nodes and Data Access nodes have function properties
        getVarNameMatch(oldName, newName, node);
      }

    });

    // Update variable references in link conditions
    exportNode.versions[1].flow.links.forEach((link) => {
      // Each link has a condition property array
      link.props.config.condition.forEach((cond) => {
        // Each condition array has an array of logic strings
        cond.forEach((c, i) => {
          if (depType === DesignItemType.variable && c.match(oldVarNamePattern)){
            cond[i] = c.replace(oldVarNamePattern, 'app.'+newName);
          }
          if (depType === DesignItemType.intent && c.match(oldIntentNamePattern)){
            cond[i] = c.replace(oldIntentNamePattern, newName);
            if (link.props.config.name && link.props.config.name.match(oldIntentNamePattern)) {
              link.props.config.name = link.props.config.name.replace(oldIntentNamePattern, newName);
            }

          }
        });
      });
      if (depType === DesignItemType.variable){
        link.props.config.serializedCondition = link.props.config.serializedCondition.replace(oldVarNamePattern, 'app.'+newName);
      }
      if (depType === DesignItemType.intent){
        link.props.config.serializedCondition = link.props.config.serializedCondition.replace(oldIntentNamePattern, newName);
      }

      // Update link inputs
      link.props.config.inputs.forEach((inp) => {
        if (inp.argumentName === 'app.'+oldName){
          inp.argumentName = 'app.'+newName;
          inp.varName = 'app.'+newName;
        }
      });

    });

    return exportNode;
  };

  /**
     * Update variable name match in any given node
    */
  const getVarNameMatch = (
    oldName: string,
    newName: string,
    node: Dependency['node'] | DesignModuleNode
  ) => {

    const oldNamePattern = new RegExp(`\\bapp.${oldName}\\b`, 'g');
    const oldNamePatternScript = new RegExp(`\\b${oldName}\\b`, 'g');

    // Reusable function scripts can contain references to application variables through PE API just like custom node scripts
    if ('script' in node){
      node.script = node.script.replace(oldNamePatternScript, newName);
    }
    // Reusable functions can have parameters that reference an app variable with app.
    if ('parameters' in node){
      node.parameters!.forEach((param) => {
        if (param.value === 'app.'+oldName){
          param.value = 'app.'+newName;
        }
      });
    }

    if ('type' in node && node.type === DesignItemType.object){
      const dependencyObject = node as DesignObject;
      // Custom nodes nodes have function properties
      if (dependencyObject.props.config.function){
        if (dependencyObject.props.config.function.arguments){
          dependencyObject.props.config.function.arguments.forEach((arg) => {
            if (arg.varName === 'app.'+oldName){
              arg.varName = 'app.'+newName;
            }
          });
        }
        if (dependencyObject.props.config.function.outputs){
          dependencyObject.props.config.function.outputs.forEach((output) => {
            if (output.varName === 'app.'+oldName){
              output.varName = 'app.'+newName;
            }
          });
        }

        if (dependencyObject.props.config.function.script){
          dependencyObject.props.config.function.script = dependencyObject.props.config.function.script.replace(oldNamePatternScript, newName);
        }
      }

      // e.g., data access node
      if (dependencyObject.props.config.customScript){
        dependencyObject.props.config.customScript = dependencyObject.props.config.customScript.replace(oldNamePatternScript, newName);
      }
      // Prompt nodes have channel and text properties
      if (dependencyObject.props.config.channel){
        const channelKeys = Object.keys(dependencyObject.props.config.channel) as DesignChannelType[];

        channelKeys.forEach((ch) => {
          const langKeys = Object.keys(dependencyObject.props.config.channel![ch]!) as LanguageCode[];

          langKeys.forEach((lang) => {
            const localizedField = dependencyObject.props.config.channel![ch]![lang];
            if (typeof localizedField === 'string') {
              dependencyObject.props.config.channel![ch]![lang] = localizedField.replace(oldNamePattern, 'app.' + newName);
            }
          });
        });
      }

      if (dependencyObject.props.config.text){
        const langKeys = Object.keys(dependencyObject.props.config.text) as LanguageCode[];
        langKeys.forEach((lang) => {

          dependencyObject.props.config.text![lang] = dependencyObject.props.config.text![lang]!.replace(oldNamePattern, 'app.'+newName);
        });
      }
    }
    // Inputs and Outputs can appear on both module and object types
    if ('type' in node && (node.type === DesignItemType.module || node.type === DesignItemType.object)){
      if (node.props.config.inputs){
        node.props.config.inputs.forEach((input) => {
          if (input.varName === 'app.'+oldName){
            input.varName = 'app.'+newName;
          }
          if (node._class === ObjectClass.prompt && input.argumentName === 'app.'+oldName){
            input.argumentName = 'app.'+newName;
          }
        });
      }
      if (node.props.config.outputs){
        node.props.config.outputs.forEach((output) => {
          if (output.varName === 'app.'+oldName){
            output.varName = 'app.'+newName;
          }
        });
      }
    }

    //return node;
  };
  /**
     * Update dependency name matches
    */
  const getDepNameMatch = (oldName: string, newName: string, depType: string) => {

    exports._dependencies.forEach((export_dep) => {
      // If export_dep matches old name, update it
      if (export_dep.node == null){
        return;
      }
      if (export_dep.node.name === oldName){
        export_dep.node.name = newName;
        if (export_dep.type === DesignItemType.variable
          && 'props' in export_dep.node
          && 'name' in export_dep.node.props.config
          && 'varName' in export_dep.node.props.config){

          export_dep.node.props.config.name = export_dep.node.name;
          export_dep.node.props.config.varName = export_dep.node.name;
        }
      }

      // If current depType variable, update name in all references in export_dep
      if (depType === DesignItemType.variable){
        getVarNameMatch(oldName, newName, export_dep.node);

      }
      // Update flow node version of dependency. Update variable references if dependency is a variable
      if ('versions' in export_dep.node){
        getFlowNameMatch(oldName, newName, depType, export_dep.node);
      }
    });

    return exports._dependencies;
  };

  /**
     * Update all flow instances of common modules with new parent ref
    */

  const installCommonModule = async (depModule: DesignModule) => {
    const { repoName, repoVersion } = depModule;
    if (!repoName || !repoVersion) {
      return;
    }

    const modules = window.apm.serialized.components.modules;
    const commonModule = await avsService.getCommonModuleDesignByName(repoName, repoVersion);

    await commonModuleManager.importCommonModule(commonModule, depModule);
    const currentCMId = window.apm.serialized.components.modules.find((mod: DesignModule) => mod.name === depModule.name)?.id;
    modules.forEach((module: DesignModule) => {
      if (module.versions[1].flow.nodes.find((mod) => mod.name === depModule.name)){
        const commonModule = module.versions[1].flow.nodes.find((mod) => mod.name === depModule.name);
        if (commonModule){
          commonModule.props.moduleId = currentCMId;
          commonModule.props.parentComponentID = currentCMId;
          commonModule.props.ref = currentCMId;
        }
      }
    });
  };

  /**
    *
    * @param {*} prompt CORE-29584 Parent prompt using this ID update all the instances of the prompt with channel config
    */
  const updatePromptInstances = (prompt: DesignObject) => {
    const updateModulePrompt = (importModule: DesignModule) => {
      importModule.versions[1].flow.nodes.filter((n) =>
        n.props.parentComponentID === prompt.id
      ).forEach((promptInstance) => {
        promptInstance.props.config = prompt.props.config;
        promptInstance.props.lastLocalChange = window.apm.timestamp;
      });
    };

    Object.values(mapping.module).forEach((actionAndId: string) => {
      const [, moduleId] = actionAndId.split('::');
      updateModulePrompt(
        exports._dependencies.find((element) =>
          element.id === moduleId
        )!.node as DesignModule
      );
    });

    updateModulePrompt(exports.node);
  };

  /**
     * Add to application
     * @param event
     */
  const setChanges = () => {
    const treeDom = $('#tl_container');
    const shouldUpgradeOmniChannel = window.apm.serialized.omniChannel.isOmniChannel;

    (Object.keys(mapping) as DependencyType[]).forEach((objType) => {
      Object.keys(mapping[objType]).forEach((objId) => {
        switch (objType) {
          case 'variable': {
            const opVar = mapping.variable[objId].split('::')[0];
            const targetIdVar = mapping.variable[objId].split('::')[1];
            const depVarIdx = exports._dependencies.findIndex((element) => element.id === objId);
            const depVar = exports._dependencies[depVarIdx].node;
            let targetVar = window.apm.getVariable(targetIdVar, null);

            if (opVar === 'create' && 'props' in depVar) {
              depVar.props.usedIn = [];
              targetVar = depVar;
              targetVar.props.createdTime = window.apm.timestamp;
              targetVar.props.lastLocalChange = window.apm.timestamp;
              if (shouldUpgradeOmniChannel && !targetVar.props.config.scope){
                targetVar.props.config.scope = 'session';
                targetVar.props.lastLocalChange = window.apm.timestamp;
              }
              treeDom.jstree().create_node('variables', Utility.getTreeNode(targetVar), 'last');
            }
            targetVar.props.usedIn.push(exports.node.id);
            window.apm.setNode(targetVar.id, targetVar);
            break;
          }
          case 'object': {
            const opObj = mapping.object[objId].split('::')[0];
            const targetIdObj = mapping.object[objId].split('::')[1];
            const depObjectIdx = exports._dependencies.findIndex((element) => element.id === objId);
            const depObject = exports._dependencies[depObjectIdx].node as DesignObject;
            if (!depObject){
              return;
            }
            let targetObject = Utility.getNode(targetIdObj, 'object') as DesignObject;
            if (opObj === 'create') {
              targetObject = depObject;
              targetObject.props.createdTime = window.apm.timestamp;
              targetObject.props.lastLocalChange = window.apm.timestamp;
              if (!treeDom.jstree().get_node(targetObject.props.nodeTypeName)) {
                // add type name folder if not exist
                treeDom.jstree().create_node('objects', {
                  'id': targetObject.props.nodeTypeName,
                  'text': targetObject.props.nodeTypeName,
                  'type': 'objects',
                  'state': { 'opened': false },
                  'children': [Utility.getTreeNode(targetObject)]
                }, 'last');
              } else {
                treeDom.jstree().create_node(targetObject.props.nodeTypeName, Utility.getTreeNode(targetObject), 'last');
              }

            }
            window.apm.setNode(targetObject.id, targetObject);
            break;
          }
          case 'function': {
            const opFunc = mapping.function[objId].split('::')[0];
            const targetIdFunc = mapping.function[objId].split('::')[1];
            const depFuncIdx = exports._dependencies.findIndex((element) => element.id === objId);
            const depFunc = exports._dependencies[depFuncIdx].node as DesignFunction;
            let targetFunc = Utility.getNode(targetIdFunc, 'function') as DesignFunction;
            if (opFunc === 'create') {
              targetFunc = depFunc;
              targetFunc.createdTime = window.apm.timestamp;
              targetFunc.lastLocalChange = window.apm.timestamp;
              window.apm.serialized.functions.push(targetFunc);
            }
            break;
          }
          case 'intent': {
            const opIntent = mapping.intent[objId].split('::')[0];
            const targetIdIntent = mapping.intent[objId].split('::')[1];
            const depIntentIdx = exports._dependencies.findIndex((element) => element.id === objId);
            const depIntent = exports._dependencies[depIntentIdx].node as DesignIntent;
            let targetIntent = Utility.getNode(targetIdIntent, 'intent') as DesignIntent;
            if (opIntent === 'create') {
              targetIntent = depIntent;
              targetIntent.isHidden = false;
              targetIntent.isGlobalIntent = false;
              targetIntent.props.createdTime = window.apm.timestamp;
              targetIntent.props.lastLocalChange = window.apm.timestamp;
              window.apm.addNode(targetIntent);
            }
            break;
          }
          case 'schema': {
            const opSchema = mapping.schema[objId].split('::')[0];
            const targetIdSchema = mapping.schema[objId].split('::')[1];
            const depSchemaIdx = exports._dependencies.findIndex((element) => element.id === objId);
            const depSchema = exports._dependencies[depSchemaIdx].node as DesignGraphQlSchema;
            let targetSchema = Utility.getNode(targetIdSchema, 'schema') as DesignGraphQlSchema;
            if (opSchema === 'create') {
              targetSchema = depSchema;
              targetSchema.createdTime = window.apm.timestamp;
              targetSchema.lastLocalChange = window.apm.timestamp;
              window.apm.serialized.graphQLSchemas.push(targetSchema);
            }
            break;
          }
          case 'csvData': {
            const opCsv = mapping.csvData[objId].split('::')[0];
            const targetIdCsv = mapping.csvData[objId].split('::')[1];
            const depCsvIdx = exports._dependencies.findIndex((element) => element.id === objId);
            const depCsv = exports._dependencies[depCsvIdx].node as DesignCsvData;
            let targetCsv = Utility.getNode(targetIdCsv, 'csvData') as DesignCsvData;
            if (opCsv === 'create') {
              targetCsv = depCsv;
              targetCsv.createdTime = window.apm.timestamp;
              targetCsv.lastLocalChange = window.apm.timestamp;
              window.apm.serialized.csvData.push(targetCsv);
            }
            break;
          }
          case 'module': {
            const opMod = mapping.module[objId].split('::')[0];
            const targetIdMod = mapping.module[objId].split('::')[1];
            const depModuleIdx = exports._dependencies.findIndex((element) => element.id === objId);
            const depModule = exports._dependencies[depModuleIdx].node as DesignModule;
            let targetModule = Utility.getNode(targetIdMod, 'module') as DesignModule;

            if (opMod === 'create' && !depModule.props.isCommonModule) {

              targetModule = JSON.parse(JSON.stringify(depModule));
              targetModule.props.createdTime = window.apm.timestamp;
              targetModule.props.lastLocalChange = window.apm.timestamp;
              window.apm.setNode(targetModule.id, targetModule);
            }
            break;
          }
        }
      });
    });

    if (exports.node.id !== rootModuleId) {
      if (exports.node.props.isCommonModule && !exports.node.isHidden) {
        if (window.apm.serialized.appCommonModules !== undefined) {
          const commonModuleAppIdx = window.apm.serialized.appCommonModules.findIndex((element: DesignCommonModule) => element._id === exports.node.props.moduleId);
          const commonModuleIdx = exports._commonModules.findIndex((element) => element._id === exports.node.props.moduleId);
          if (commonModuleIdx !== -1 && commonModuleAppIdx === -1) {

            window.apm.serialized.appCommonModules.push(exports._commonModules[commonModuleIdx]);
          }

        }
      }
      exports.node.props.createdTime = window.apm.timestamp;
      exports.node.props.lastLocalChange = window.apm.timestamp;
      exports.node.props.lastSavedChange = 0;

      treeDom.jstree().create_node('modules', Utility.getTreeNode(exports.node), 'last');
    }

    window.apm.setNode(exports.node.id, exports.node);
    window.apm.postEventToAllViews({ target: 'request-reload-data-model' });

    Object.keys(mapping['module']).forEach((objId) => {
      const opMod = mapping.module[objId].split('::')[0];
      const depModuleIdx = exports._dependencies.findIndex((element) => element.id === objId);
      const depModule = exports._dependencies[depModuleIdx].node as DesignModule;

      if (opMod === 'create' && depModule.props.isCommonModule) {
        installCommonModule(depModule);
      }
    });
    window.apm.setSerializedChanged();
    window.apm.refreshApplicationTree();
    window.apm.reactDOMComponents.renderApplicationPages();
  };
  initializeChanges();
}

/**
    *  Update dependency names and all references in the import
    * @param {array} nodes main node, flows, and dependencies
    */

export function updateDependencyName(
  node: Dependency['node'] | DesignModuleNode,
  nodeType: string
) {

  if (!('name' in node)){
    return false;
  }
  const dependencyName = 'name' in node ? node.name : null;
  const componentList = window.apm.getAttachments(nodeType.toLowerCase()) || [];

  //Checking serialized for object match by node name
  const importMatch = componentList.findIndex((element: (DesignObject | DesignModule)) => element.name === dependencyName);

  // If dependency name does exist in application, we give it a unique name
  if (importMatch !== -1){
    // Check if importing dependency name exists in application with "_IMPORT"
    const depAppNamesWithImport = componentList.filter((element: (DesignObject | DesignModule)) => element.name!.includes(dependencyName+'_IMPORT'));
    const depAppNamesLength = depAppNamesWithImport.length;
    const importNameMatch = depAppNamesLength > 0 ?  depAppNamesWithImport[depAppNamesLength - 1].name : false;
    // Check if importing dependency name itself exists with "_IMPORT"
    const importNameSearch = /_IMPORT(\d+)$/;

    if (dependencyName && importNameSearch.test(dependencyName) || importNameMatch) {
      // Increment number on dependency name
      const nameToIncrement = importNameSearch.test(dependencyName!) ? dependencyName: importNameMatch;
      const digit = parseInt(importNameSearch.exec(nameToIncrement)![1], 10);

      node.name = nameToIncrement.replace(importNameSearch, `_IMPORT${digit + 1}`);

    } else {
      node.name += '_IMPORT1';
    }
  } else {
    return null;
  }

  return node;
}
